import React, { useEffect, useReducer } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";

import Error from "../../components/Error/Error";
import EorDetails from "../../components/EorDetails/EorDetails";
import Epayment from "../../services/api/epayment.service";
import usePaymentUpdates from "../../services/hooks/usePaymentUpdates";
import appConfig, { formatPhpAmount } from "../../config/app.config";

import "./Information.css";

const initialStatus = {
  text: "",
  color: "default",
};

// change status display and color base on status code
const statusReducer = (currentStatus, action) => {
  switch (action.type) {
    case "UPDATE_STATUS": {
      if (!action.text) return initialStatus;
      if (action.text === "FOR PAYMENT" || action.text === "I") {
        return { text: "FOR PAYMENT", color: "payment", data: action.payload };
      }

      if (action.text === "C") {
        return {
          ...currentStatus,
          text: "CANCELLED",
          color: "cancelled",
          data: action.payload,
        };
      }

      if (action.text === "F") {
        return {
          ...currentStatus,
          text: "FAILED",
          color: "failed",
          data: action.payload,
        };
      }

      if (action.text === "E") {
        return {
          ...currentStatus,
          text: "EXPIRED",
          color: "expired",
          data: action.payload,
        };
      }

      if (action.text === "S") {
        return {
          ...currentStatus,
          text: "PAID",
          color: "success",
          data: action.payload,
        };
      }

      // ! If status is "paid" in the database, but not yet paid in the payment gateway it would result to under evaluation
      return {
        ...currentStatus,
        data: action.payload,
        text: "UNDER EVALUATION",
        color: "evaluation",
      };
    }

    default:
      return new Error("Action not allowed");
  }
};

// loading swal alert
const loadingData = (title, html) => {
  Swal.fire({
    title,
    html,
    allowOutsideClick: false,
    showConfirmButton: false,
    scrollbarPadding: false,
    willOpen: () => {
      Swal.showLoading();
    },
  });
};

// error swal alert
const showSwalError = (icon, title, text) => {
  Swal.fire({
    icon,
    title,
    text,
    scrollbarPadding: false,
  });
};

// information page
function Information() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, dispatch] = useReducer(statusReducer, initialStatus);
  const { userData } = location.state || {};
  const { data: paymentData, connection } = usePaymentUpdates(
    userData?.billing_no,
  );
  const currentBill = { ...userData, ...paymentData };
  const allowPayBtn = ["FOR PAYMENT", "CANCELLED", "FAILED", "EXPIRED"];

  const { qcuserid } = useParams();

  const allowStatusBtn = ["PAID", "CANCELLED", "FAILED", "EXPIRED"];

  const {
    amount,
    reference_no: refNumber,
    billing_no: billingNo,
  } = currentBill;

  const showBtn = (arr1, data) => arr1.includes(data);

  const getDetailCode = (parameterName) => {
    const value = new URLSearchParams(location.search).get(parameterName);
    if (!value) {
      return "";
    }
    // Per AC-REDESIGN0730-S2: trim whitespace AND strip surrounding single/double
    // quotes. Order matters — trim first to expose any outer quotes, strip the
    // quotes, then trim again to remove whitespace that was inside the quotes.
    return value
      .trim()
      .replace(/^['"]+|['"]+$/g, "")
      .trim();
  };

  const payOnlineHandler = async (e) => {
    e.preventDefault();

    const confirmed = await Swal.fire({
      title: "Please confirm!",
      icon: "info",
      showCancelButton: true,
      scrollbarPadding: false,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    loadingData("Loading", "Please wait while we are processing your request.");

    try {
      const requiredDataForAccess = {
        reference_number: billingNo,
      };
      const officecodes = getDetailCode("officecodes");
      const accountcodes = getDetailCode("accountcodes");

      if (officecodes && accountcodes) {
        requiredDataForAccess.detail_codes = { officecodes, accountcodes };
      }

      // check if billing number is already paid
      try {
        const res = await Epayment.paymentRequestAccess(requiredDataForAccess);
        window.open(res.data.data.transaction_url);
        Swal.close();
      } catch (err) {
        if (
          err?.code === "PAYMENT_SESSION_REQUIRED" ||
          [401, 403].includes(err?.response?.status)
        ) {
          showSwalError(
            "error",
            "Payment access required",
            "Unable to access payment updates. Please reopen your payment link.",
          );
        } else if (
          err?.response?.data?.message === "Billing number is already paid."
        ) {
          showSwalError(
            "warning",
            "Payment Already Made",
            "We apologize, but it appears that the payment for this billing number has already been made. Please check your records and ensure that the correct billing number was entered. If you believe that this is an error, please contact our support team for assistance.",
          );
        } else {
          showSwalError(
            "error",
            "Error",
            "Something went wrong. Please try again later.",
          );
        }
      }
    } catch (error) {
      showSwalError(
        "error",
        "Error",
        "Something went wrong. Please try again later.",
      );
    }
  };

  useEffect(() => {
    dispatch({
      type: "UPDATE_STATUS",
      text: paymentData?.status || "",
      payload: paymentData,
    });
  }, [paymentData]);

  let content;

  // display content if userData is not empty else, display error page
  if (userData && userData.length !== 0) {
    content = (
      <section className="information border--blue">
        <Row className="information--main">
          <Col className="information-payment" xl={6} xs={12}>
            <div className="information-payment-card">
              <p className="information-eyebrow">ORDER OF PAYMENT</p>
              <h1>Ready to complete your payment?</h1>
              <p className="information-description">
                Review your order details, then select an option below to
                continue.
              </p>
              <div className="information-status">
                <span>Current status</span>
                <span className={`statusColor-${status.color}`}>
                  {status.text || "CHECKING"}
                </span>
              </div>
            </div>
            {connection === "reconnecting" && (
              <p role="status">Reconnecting to payment updates…</p>
            )}
            {connection === "denied" && (
              <p role="alert">
                Unable to access payment updates. Please reopen your payment
                link.
              </p>
            )}
            <div className="information-actions">
              {showBtn(allowPayBtn, status.text) && (
                <Button
                  onClick={(event) => payOnlineHandler(event)}
                  className="information-primary-button"
                  variant="contained"
                  size="large"
                  disableElevation
                  data-testid="information-pay-online-button"
                >
                  Pay online
                </Button>
              )}

              {showBtn(allowStatusBtn, status.text) && (
                <Button
                  onClick={() =>
                    navigate(`/status/${qcuserid}`, {
                      state: { transData: status.data, userData: currentBill },
                    })
                  }
                  className="information-secondary-button"
                  variant="outlined"
                  size="large"
                  disableElevation
                  data-testid="information-check-status-button"
                >
                  Check Status
                </Button>
              )}
            </div>
          </Col>

          <Col className="information-summary" xl={6} xs={12}>
            <div className="information-summary-card">
              <div className="information-summary-heading">
                <p className="information-eyebrow">PAYMENT DETAILS</p>
                <h2>Payment summary</h2>
              </div>
              <TableContainer className="information-table-container">
                <Table aria-label="Payment summary">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        className="information-label"
                        component="th"
                        scope="row"
                      >
                        Billing number
                      </TableCell>
                      <TableCell className="information-value" align="right">
                        {billingNo}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="information-label"
                        component="th"
                        scope="row"
                      >
                        Reference number
                      </TableCell>
                      <TableCell className="information-value" align="right">
                        {refNumber}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="information-label"
                        component="th"
                        scope="row"
                      >
                        Total amount
                      </TableCell>
                      <TableCell className="information-amount" align="right">
                        {formatPhpAmount(amount)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="information-label"
                        component="th"
                        scope="row"
                      >
                        Status
                      </TableCell>
                      <TableCell align="right">
                        <span className={`statusColor-${status.color}`}>
                          {status.text || "CHECKING"}
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <EorDetails details={currentBill.eor_details} />
            </div>
          </Col>
        </Row>

        <button
          type="button"
          className="information-back-button"
          onClick={() => navigate(`${appConfig.app.subDir}${qcuserid}`)}
          data-testid="information-back-button"
        >
          <ArrowBackIcon />
          Back to billing search
        </button>
      </section>
    );
    /* Setting the `content` variable to an error page if the `userData` is empty
  or undefined. The error page displays a message that the user does not have permission 
  to view the site and provides a button to return. */
  } else {
    content = (
      <Error
        header="Access Denied"
        description="You don't have permission to view this site"
        btnTxt="RETURN"
      />
    );
  }

  return content;
}

export default Information;
